import { AudioManager } from "./AudioManager";
import { AuthManager } from "./AuthManager";
import { PaymentManager } from "./PaymentManager";
import {
	appStore,
	useAppStore,
	type AppStoreProps,
	type AppStore,
	type ModalIds,
} from "./stores/appStore";
import type { PlaybackStore } from "./stores/playbackStore";
import { type TimerStore, timerStore } from "./stores/timerStore";

/**
 * AppManager is a singleton class that manages the application state and
 * interactions between different stores. Its main responsibility is provide
 * access to the underlying managers and to orchestrate the interactions between
 * them.
 */
export class AppManager {
	private static instance: AppManager | null = null;
	// move this as a pre-initialized timer in the store
	private adTimerId = "AD_TIMER";
	private appStore: AppStore;
	private playbackStore: PlaybackStore;
	private timerStore: TimerStore;

	audioManager: AudioManager;
	authManager = AuthManager;
	paymentManager = PaymentManager;

	useStore: () => AppStoreProps;

	static getInstance(): AppManager {
		if (!AppManager.instance) {
			AppManager.instance = new AppManager();
		}
		return AppManager.instance;
	}

	// setup our ad timer
	// TODO: just have this created in the store by default
	private createAdTimer = () => {
		this.timerStore
			.getState()
			.createTimer(this.adTimerId, import.meta.env.VITE_AD_TIMEOUT ?? 20 * 60);
	};

	// setup playback triggers
	private playbackStateListener = () => {
		const handlePlaybackStatusChange = async (status: string) => {
			const isSubscribed = this.appStore.getState().isUserSubscribed;
			if (isSubscribed) {
				// If user is subscribed, ignore ad timer
				return;
			}

			const timer = this.timerStore.getState(); // use shorthand to avoid repeat calls

			if (status === "PLAYING") {
				timer.startTimer(this.adTimerId);
			}

			if (status === "PAUSED") {
				timer.pauseTimer(this.adTimerId);
			}
		};

		// Subscribe to the playback store for only status changes
		this.playbackStore.subscribe(
			(state) => state.status,
			handlePlaybackStatusChange,
		);
	};

	private addTimerListener = async () => {
		// subscribe to the ad timer remaining time
		this.timerStore.subscribe(
			(state) => state.timers[this.adTimerId].remainingTime,
			async (remainingTime) => {
				const isSubscribed = this.appStore.getState().isUserSubscribed;

				// Prevent triggering ads for subscribed users
				if (isSubscribed) return;

				const playback = this.playbackStore.getState();

				if (remainingTime <= 0 && playback.status === "PLAYING") {
					const adTrackOnEndCallback = () => {
						this.appStore.getState().showModal("INTERRUPTION_MODAL", false);
						// Reset and restart ad timer
						this.timerStore.getState().resetTimer(this.adTimerId);
						this.timerStore.getState().startTimer(this.adTimerId);
					};

					await this.audioManager.pauseTrack();
					this.appStore.getState().showModal("INTERRUPTION_MODAL", true);
					await this.audioManager.playAdTrack(adTrackOnEndCallback);
				}
			},
		);
	};

	showModal = (id: ModalIds, isVisible: boolean) => {
		this.appStore.getState().showModal(id, isVisible);
	};

	// setIsSubscribed = (isSubscribed: boolean) => {
	// 	this.appStore.getState().setUserSubscriptionStatus(isSubscribed);

	// 	if (isSubscribed) {
	// 		// prevent any unwanted modal or timer behavior
	// 		this.timerStore.getState().pauseTimer(this.adTimerId);
	// 		this.timerStore.getState().resetTimer(this.adTimerId);
	// 	}
	// };

	private initialize() {
		this.createAdTimer();
		this.playbackStateListener();
		this.addTimerListener();
	}

	private constructor() {
		this.audioManager = AudioManager.getInstance({
			appStore,
			showModalHandler: this.showModal,
		});
		this.appStore = appStore;
		this.useStore = useAppStore;
		this.playbackStore = this.audioManager.store;
		this.timerStore = timerStore;

		AuthManager.initialize();

		this.initialize();
	}
}

export type AppManagerType = AppManager;
